import React, { useState, useEffect, useCallback, useRef } from "react";
import { Typography, Flex, Input, Button, Table, DatePicker, Tooltip, Empty, message, Spin } from "antd";
import { SearchOutlined, DownloadOutlined, MailOutlined, CheckCircleOutlined, FileDoneOutlined, EditOutlined, AuditOutlined } from "@ant-design/icons";
import { getInvoices, updateInvoice } from "../../../services/invoiceService";
import { getClients } from "../../../services/clientService";
import useAuthSession from "../../../hooks/useAuthSession";
import InvoiceModal from "../invoice/InvoiceModal";
import GstFilingModal from "../invoice/GstFilingModal";

const { Title, Text } = Typography;

const STATUS_CONFIG_MAP = {
  paid: { color: "#16a34a", label: "Paid" },
  "partially paid": { color: "#d97706", label: "Partially Paid" },
  partial: { color: "#d97706", label: "Partially Paid" },
  overdue: { color: "#dc2626", label: "Overdue" },
  unpaid: { color: "#ea580c", label: "Unpaid" },
};

const getPaymentStatusConfig = (status) => {
  const s = String(status || "unpaid").toLowerCase();
  return STATUS_CONFIG_MAP[s] || { color: "#ea580c", label: s.charAt(0).toUpperCase() + s.slice(1) };
};

function InvoicesView({ role: propRole, initialSearch = "" }) {
  const { companyCode: effectiveCompanyCode, employeeId, isAdmin } = useAuthSession();
  const isUserAdmin = propRole ? (propRole === "admin" || propRole === "company_admin" || propRole === "superadmin") : isAdmin;

  const [invoices, setInvoices] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedClient, setSelectedClient] = useState(null);
  const [invoiceCompanies, setInvoiceCompanies] = useState([]);
  const [totalAllInvoicesCount, setTotalAllInvoicesCount] = useState(0);
  const [railLoading, setRailLoading] = useState(true);
  const [invoiceSearch, setInvoiceSearch] = useState(initialSearch);
  const [dateFilter, setDateFilter] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isGstFilingModalOpen, setIsGstFilingModalOpen] = useState(false);
  const [modalPrefillClient, setModalPrefillClient] = useState(null);
  const [selectedInvoiceRecord, setSelectedInvoiceRecord] = useState(null);

  const activeRequestIdRef = useRef(0);
  const activeAbortControllerRef = useRef(null);

  // Clean up any pending network request when component unmounts
  useEffect(() => {
    return () => {
      if (activeAbortControllerRef.current) {
        activeAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Fetch all clients for left rail
  const loadClientsList = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    try {
      const res = await getClients(effectiveCompanyCode);
      const list = res?.clients || res?.items || res?.data || (Array.isArray(res) ? res : []);
      setAllClients(list);
    } catch (err) {
      console.error("Failed to load clients:", err);
    }
  }, [effectiveCompanyCode]);

  useEffect(() => {
    loadClientsList();
  }, [loadClientsList]);

  // Sync search when parent navigates here from a global search result
  useEffect(() => {
    if (initialSearch !== undefined) {
      setInvoiceSearch(initialSearch);
      setCurrentPage(1);
    }
  }, [initialSearch]);

  // Fetch Live Invoices with Server-side Pagination & Race-condition Guard
  const loadInvoices = useCallback(async () => {
    if (!effectiveCompanyCode) return;

    // Abort previous in-flight request so outdated responses cannot overwrite the active selection
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    activeAbortControllerRef.current = controller;

    const currentRequestId = ++activeRequestIdRef.current;
    setLoading(true);

    try {
      const options = {
        page: currentPage,
        pageSize: pageSize,
        signal: controller.signal,
      };
      if (employeeId) options.employeeId = employeeId;
      if (selectedClient) {
        if (selectedClient.companyName && selectedClient.companyName !== "Client Enterprise") {
          options.companyName = selectedClient.companyName;
        }
        if (selectedClient.clientId) {
          options.clientId = selectedClient.clientId;
        }
      }
      if (invoiceSearch.trim()) options.search = invoiceSearch.trim();
      if (dateFilter) {
        options.dateFrom = dateFilter.format("YYYY-MM-DD");
        options.dateTo = dateFilter.format("YYYY-MM-DD");
      }

      const res = await getInvoices(effectiveCompanyCode, options);

      // Discard stale response if a newer request has started
      if (currentRequestId !== activeRequestIdRef.current) {
        return;
      }

      if (res.success) {
        setInvoices(res.invoices || []);
        setTotalCount(res.total || (res.invoices || []).length);
        if (!selectedClient && !invoiceSearch.trim() && !dateFilter) {
          setTotalAllInvoicesCount(res.total || (res.invoices || []).length);
        }
      }
    } catch (err) {
      if (controller.signal.aborted || currentRequestId !== activeRequestIdRef.current) {
        return;
      }
      console.error("Failed to load invoices from middleware:", err);
      message.error("Failed to fetch live invoices.");
    } finally {
      if (currentRequestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [effectiveCompanyCode, employeeId, selectedClient, invoiceSearch, dateFilter, currentPage, pageSize]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Load full invoices list to extract unique companies that actually have invoices
  const loadInvoiceCompanies = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setRailLoading(true);
    try {
      const res = await getInvoices(effectiveCompanyCode, { page: 1, pageSize: 300 });
      if (res.success) {
        const invs = res.invoices || [];
        setTotalAllInvoicesCount(res.total || invs.length);

        const compMap = new Map();
        invs.forEach((inv) => {
          const rawComp = (inv.leadCompanyName || inv.companyName || inv.clientCompanyName || inv.clientSnapshot?.companyName || "").trim();
          const rawContact = (inv.contactName || inv.clientSnapshot?.contactName || "").trim();
          const key = (rawComp || inv.clientId || "Client Enterprise").toLowerCase();

          if (!compMap.has(key)) {
            const matchedClient = allClients.find(
              (c) =>
                (c.companyName && c.companyName.trim().toLowerCase() === rawComp.toLowerCase()) ||
                (c.clientId && inv.clientId && c.clientId === inv.clientId)
            );

            compMap.set(key, {
              key,
              clientId: inv.clientId || matchedClient?.clientId || key,
              companyName: rawComp || matchedClient?.companyName || "Client Enterprise",
              leadCompanyName: rawComp || matchedClient?.companyName || "Client Enterprise",
              contactName: rawContact || matchedClient?.primaryContactName || matchedClient?.contactName || "Contact Person",
              primaryContact: rawContact || matchedClient?.primaryContactName || matchedClient?.contactName || "Contact Person",
              phone: inv.contactNumber || inv.clientSnapshot?.phone || matchedClient?.primaryPhone || "",
              email: inv.directorEmailAddress || inv.clientSnapshot?.email || matchedClient?.primaryEmail || "",
              invoiceCount: 1,
            });
          } else {
            compMap.get(key).invoiceCount += 1;
          }
        });

        const list = Array.from(compMap.values()).sort((a, b) => b.invoiceCount - a.invoiceCount);
        setInvoiceCompanies(list);
      }
    } catch (err) {
      console.error("Failed to load invoice companies:", err);
    } finally {
      setRailLoading(false);
    }
  }, [effectiveCompanyCode, allClients]);

  useEffect(() => {
    loadInvoiceCompanies();
  }, [loadInvoiceCompanies]);

  // Dynamic clients in left rail ONLY includes companies with invoices
  const dynamicClients = invoiceCompanies;
  const filteredClients = dynamicClients;

  const handleSelectClient = (client) => {
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }
    activeRequestIdRef.current++;

    const isSameSelected = Boolean(
      client &&
        selectedClient &&
        ((selectedClient.key && selectedClient.key === client.key) ||
          (selectedClient.companyName && client.companyName && selectedClient.companyName.toLowerCase() === client.companyName.toLowerCase()))
    );

    if (!client || isSameSelected) {
      setSelectedClient(null);
    } else {
      setSelectedClient(client);
    }
    setInvoices([]);
    setTotalCount(0);
    setLoading(true);
    setCurrentPage(1);
  };

  const handleInvoiceSearchChange = (val) => {
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }
    activeRequestIdRef.current++;
    setInvoiceSearch(val);
    setInvoices([]);
    setTotalCount(0);
    setLoading(true);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (d) => {
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }
    activeRequestIdRef.current++;
    setDateFilter(d);
    setInvoices([]);
    setTotalCount(0);
    setLoading(true);
    setCurrentPage(1);
  };

  const handleInvoiceCreated = () => {
    loadInvoices();
  };

  const handleMarkPaid = async (record) => {
    try {
      const updated = await updateInvoice(effectiveCompanyCode, record._id, {
        paymentStatus: "paid",
        amountPaid: Number(record.total || 0),
        balanceDue: 0,
      });
      if (updated?.success !== false) {
        message.success(`Invoice ${record.invoiceNumber} marked as Paid!`);
        loadInvoices();
      } else {
        message.error("Failed to update invoice payment status.");
      }
    } catch (err) {
      console.error(err);
      message.error("Error updating invoice payment.");
    }
  };

  const handleOpenEditInvoice = (record) => {
    let enriched = { ...record };
    const compName = record.leadCompanyName || record.companyName || record.clientSnapshot?.companyName;
    const matched = dynamicClients.find((c) => c.clientId === record.clientId || (compName && c.companyName === compName));
    if (matched) {
      enriched = {
        ...enriched,
        address: enriched.address || matched.address || "",
        gstNumber: enriched.gstNumber || matched.gstNumber || "",
        clientSnapshot: {
          ...enriched.clientSnapshot,
          address: enriched.clientSnapshot?.address || matched.address || "",
          gstNumber: enriched.clientSnapshot?.gstNumber || matched.gstNumber || "",
        },
      };
    }
    setSelectedInvoiceRecord(enriched);
    setIsInvoiceModalOpen(true);
  };

  const handleOpenMail = (record) => {
    handleOpenEditInvoice(record);
  };

  // Table Columns
  const columns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (val, r) => (
        <Flex
          vertical
          style={{ cursor: "pointer" }}
          onClick={() => handleOpenEditInvoice(r)}
        >
          <Text strong style={{ color: "#2459d3", fontSize: "0.9rem" }}>
            {val || "INV-NEW"}
          </Text>
          {r.leadCode && (
            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
              Lead: {r.leadCode}
            </Text>
          )}
        </Flex>
      ),
    },
    {
      title: "Client / Company",
      dataIndex: "leadCompanyName",
      key: "leadCompanyName",
      render: (val, r) => {
        const cName = val || r.companyName || r.clientSnapshot?.companyName || "Client Enterprise";
        const contact = r.contactName || r.clientSnapshot?.contactName || "—";
        const cId = r.clientId || r.clientSnapshot?.clientId || "—";
        return (
          <Flex vertical>
            <Text strong style={{ fontSize: "0.88rem", color: "#0f172a" }}>
              {cName}
            </Text>
            <Text type="secondary" style={{ fontSize: "0.75rem" }}>
              {cId} &bull; {contact}
            </Text>
          </Flex>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      render: (val, r) => {
        const d = val || r.createdAt;
        return (
          <Text style={{ fontSize: "0.82rem" }}>
            {d
              ? new Date(d).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </Text>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (status) => {
        const { color, label } = getPaymentStatusConfig(status);
        return (
          <span style={{ color, fontWeight: 600, fontSize: "0.82rem" }}>
            {label}
          </span>
        );
      },
    },
    {
      title: "Total Amount",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (amt) => (
        <Text strong style={{ fontSize: "0.9rem" }}>
          ₹{Number(amt || 0).toLocaleString("en-IN")}
        </Text>
      ),
    },
    {
      title: "Amount Paid",
      dataIndex: "amountPaid",
      key: "amountPaid",
      align: "right",
      render: (paid) => <Text style={{ color: "#10b981", fontWeight: 600 }}>₹{Number(paid || 0).toLocaleString("en-IN")}</Text>,
    },
    {
      title: "Balance Due",
      dataIndex: "balanceDue",
      key: "balanceDue",
      align: "right",
      render: (bal, r) => {
        const b = bal !== undefined ? Number(bal) : Math.max(0, Number(r.total || 0) - Number(r.amountPaid || 0));
        return <Text style={{ color: b > 0 ? "#ef4444" : "#10b981", fontWeight: 600 }}>₹{b.toLocaleString("en-IN")}</Text>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, r) => (
        <Flex justify="end" gap={6}>
          <Tooltip title="Edit Invoice & Partial Payments">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ color: "#2459d3" }} />}
              onClick={() => handleOpenEditInvoice(r)}
            />
          </Tooltip>

          <Tooltip title="Download GST PDF">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined style={{ color: "#2459d3" }} />}
              onClick={() => handleOpenEditInvoice(r)}
            />
          </Tooltip>

          {String(r.paymentStatus || "").toLowerCase() !== "paid" && (
            <Tooltip title="Mark as Fully Paid">
              <Button type="text" size="small" icon={<CheckCircleOutlined style={{ color: "#10b981" }} />} onClick={() => handleMarkPaid(r)} />
            </Tooltip>
          )}

          <Tooltip title="Send to Client">
            <Button
              type="text"
              size="small"
              icon={<MailOutlined style={{ color: "#64748b" }} />}
              onClick={() => handleOpenMail(r)}
            />
          </Tooltip>
        </Flex>
      ),
    },
  ];

  return (
    <div className="qa-spin-wrapper">
      <Flex vertical gap={20} className="invoices-workspace-container">
        {/* ── WORKSPACE SPLIT (LEFT RAIL + RIGHT TABLE) ── */}
        <div className="qa-workspace-frame">
          {/* ── LEFT RAIL: ONBOARDED CLIENTS ── */}
          <div className="qa-rail-pane">
            <Flex justify="space-between" align="center" className="qa-rail-header">
              <Flex align="center" gap={8}>
                <FileDoneOutlined style={{ color: "#2459d3", fontSize: 16 }} />
                <Text strong style={{ fontSize: "0.95rem" }}>
                  Invoices ({totalAllInvoicesCount || totalCount})
                </Text>
              </Flex>
            </Flex>

            {/* Pinned All Invoices & Clients Option */}
            <div className="qa-rail-pinned">
              <button
                type="button"
                className={`qa-rail-item ${!selectedClient ? "active" : ""}`}
                onClick={() => handleSelectClient(null)}
                style={{ width: "100%", textAlign: "left", display: "block", fontFamily: "inherit" }}
              >
                <Text
                  strong
                  style={{
                    fontSize: "0.88rem",
                    color: !selectedClient ? "#2459d3" : "#0f172a",
                    display: "block",
                  }}
                >
                  All Invoices & Clients
                </Text>
                <Flex justify="space-between" align="center" style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ display: "block", fontSize: "0.74rem" }}>
                    View full live registry
                  </Text>
                  {(totalAllInvoicesCount || totalCount) > 0 && (
                    <span className={`qa-count-badge ${!selectedClient ? "active" : ""}`}>
                      {totalAllInvoicesCount || totalCount}
                    </span>
                  )}
                </Flex>
              </button>
            </div>

            <div className="qa-rail-list">
              {railLoading && filteredClients.length === 0 ? (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <Spin size="small" tip="Loading companies..." />
                </div>
              ) : filteredClients.length === 0 ? (
                <Empty description="No clients found" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "20px 0" }} />
              ) : (
                filteredClients.map((client) => {
                  const isSelected = Boolean(
                    selectedClient &&
                      ((selectedClient.key && selectedClient.key === client.key) ||
                        (selectedClient.companyName && client.companyName && selectedClient.companyName.toLowerCase() === client.companyName.toLowerCase()))
                  );
                  const displayName = client.companyName || client.leadCompanyName || client.primaryContact || client.clientId || "Client Enterprise";
                  const invCount = Number(client.invoiceCount || 0);
                  const contactDetails = [client.primaryContact, client.phone].filter(Boolean).join(" • ");
                  return (
                    <button
                      type="button"
                      key={client.key || client.companyName || client.clientId}
                      className={`qa-rail-item ${isSelected ? "active" : ""}`}
                      onClick={() => handleSelectClient(client)}
                      style={{ width: "100%", textAlign: "left", display: "block", fontFamily: "inherit" }}
                    >
                      <Flex vertical style={{ width: "100%" }}>
                        <Text
                          strong
                          style={{
                            fontSize: "0.88rem",
                            color: isSelected ? "#2459d3" : "#0f172a",
                            lineHeight: 1.35,
                            wordBreak: "break-word",
                          }}
                        >
                          {displayName}
                        </Text>
                        <Flex justify="space-between" align="center" style={{ width: "100%", marginTop: 4 }}>
                          <Text type="secondary" style={{ fontSize: "0.74rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, paddingRight: 6 }}>
                            {contactDetails || "—"}
                          </Text>
                          {invCount > 0 && (
                            <span className={`qa-count-badge ${isSelected ? "active" : ""}`}>
                              {invCount}
                            </span>
                          )}
                        </Flex>
                      </Flex>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* ── RIGHT CONTENT: LIVE INVOICE TABLE ── */}
          <div className="qa-content-pane">
            <Flex vertical gap={16}>
              {/* Header & Filter Row */}
              <Flex justify="space-between" align="center" wrap="wrap" gap={16} className="qa-content-header">
                <Flex vertical>
                  <Title level={4} style={{ margin: 0 }}>
                    {selectedClient ? selectedClient.companyName || "Client Invoices" : "Invoice History"}
                  </Title>
                  <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                    {totalCount} invoices found
                  </Text>
                </Flex>

                <Flex align="center" gap={12} wrap="wrap">
                  <Input
                    prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                    placeholder="Search invoices..."
                    value={invoiceSearch}
                    onChange={(e) => handleInvoiceSearchChange(e.target.value)}
                    allowClear
                    style={{ width: 180, borderRadius: 8 }}
                  />

                  <DatePicker
                    placeholder="Filter Date"
                    value={dateFilter}
                    onChange={(d) => handleDateFilterChange(d)}
                    style={{ width: 140, borderRadius: 8 }}
                  />

                  {isUserAdmin && (
                    <Button
                      type="primary"
                      icon={<AuditOutlined />}
                      onClick={() => setIsGstFilingModalOpen(true)}
                      style={{
                        backgroundColor: "#2459d3",
                        borderColor: "#2459d3",
                        fontWeight: 500,
                        borderRadius: 8,
                      }}
                    >
                      GST Filing
                    </Button>
                  )}
                </Flex>
              </Flex>

              {/* Ant Design Invoices Table */}
              {loading && invoices.length === 0 ? (
                <div style={{ padding: "80px 0", textAlign: "center" }}>
                  <Spin size="large" tip="Loading invoices..." />
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={invoices}
                  loading={{
                    spinning: loading,
                    tip: "Loading invoices...",
                    size: "large",
                  }}
                  rowKey="_id"
                  locale={{
                    emptyText: <Empty description="No invoices found for current filters" style={{ padding: "4rem 0" }} />,
                  }}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalCount,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} invoices`,
                    onChange: (page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    },
                  }}
                  className="qa-antd-table"
                  size="middle"
                  scroll={{ x: 900 }}
                />
              )}
            </Flex>
          </div>
        </div>

        {/* Invoice Builder Modal with native design and Mail attachment support */}
        <InvoiceModal
          visible={isInvoiceModalOpen}
          open={isInvoiceModalOpen}
          onClose={() => {
            setIsInvoiceModalOpen(false);
            setSelectedInvoiceRecord(null);
            setModalPrefillClient(null);
          }}
          onSaveInvoice={handleInvoiceCreated}
          record={selectedInvoiceRecord}
          savedInvoiceRecord={selectedInvoiceRecord}
          prefillClient={modalPrefillClient}
          clientsList={dynamicClients}
          companyCode={effectiveCompanyCode}
          employeeId={employeeId}
        />

        {/* GST Filing & Return Compliance Modal (Admin Only) */}
        {isUserAdmin && (
          <GstFilingModal
            open={isGstFilingModalOpen}
            onClose={() => setIsGstFilingModalOpen(false)}
            companyCode={effectiveCompanyCode}
          />
        )}
      </Flex>
    </div>
  );
}

export default InvoicesView;
